import { infiniteQueryOptions, skipToken, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { API_MAX_PAGE_LIMIT } from '@/common/constants';
import { useOrgId } from '@/common/hooks/useOrgId';
import { ObjectivesApi } from '@/features/objectives/api/objectives';
import { objectivesQueryKeys } from '@/features/objectives/queryKeys';

const objectivesInfiniteQueryOptions = ({ orgId, loopId }: { orgId: string; loopId?: number }) =>
  infiniteQueryOptions({
    queryKey:
      loopId === undefined
        ? objectivesQueryKeys.list.infinite(orgId)
        : objectivesQueryKeys.list.infiniteByLoopId(orgId, loopId),
    queryFn: ({ pageParam }) =>
      ObjectivesApi.getObjectives({ loopId, limit: API_MAX_PAGE_LIMIT, cursor: pageParam }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.cursor || undefined,
    select: (data) => data.pages.flatMap((page) => page.data),
  });

export const useObjectivesInfiniteQuery = () => {
  const orgId = useOrgId();
  return useInfiniteQuery(objectivesInfiniteQueryOptions({ orgId }));
};

export const useObjectivesByLoopIdInfiniteQuery = ({
  loopId,
  enabled = true,
}: {
  loopId: number;
  enabled?: boolean;
}) => {
  const orgId = useOrgId();
  return useInfiniteQuery({ ...objectivesInfiniteQueryOptions({ orgId, loopId }), enabled });
};

export const useObjectiveQuery = (objectiveId: number | undefined) => {
  const orgId = useOrgId();
  return useQuery({
    queryKey: objectivesQueryKeys.byId(orgId, objectiveId),
    queryFn: objectiveId !== undefined ? () => ObjectivesApi.getObjective(objectiveId) : skipToken,
    retry: false,
  });
};
